/**
 * Carregamento do índice e revalidação — o mesmo ciclo da fumaça.
 *
 * Só baixa quando a camada é ligada. Depois, revalida a cada ciclo da
 * atualização automática e quando a janela do loop anda (imagem nova do
 * satélite). O índice (7 dias) tem ~15 KB comprimido.
 */
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { BASE_RAIOS, carregarIndice, type IndiceRaios } from './api';
import { useRaiosStore } from './store';
import { avisoDeFrescor } from './tempo';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;
/** A camada registra aqui o "tente de novo os que falharam". */
let tentarDeNovo: (() => void) | null = null;

export function registrarNovaTentativa(fn: (() => void) | null): void {
  tentarDeNovo = fn;
}

function mesmoIndice(a: IndiceRaios | null, b: IndiceRaios): boolean {
  if (!a || a.quadros.length !== b.quadros.length) return false;
  return a.quadros.every((q, i) => q.c === b.quadros[i].c && q.n === b.quadros[i].n && q.a === b.quadros[i].a);
}

export async function carregarRaios(force = false): Promise<void> {
  if (!BASE_RAIOS) return;
  const { situacao } = useRaiosStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  // A revalidação é silenciosa: "Baixando…" piscando a cada ciclo é ruído.
  if (!jaCarregou) useRaiosStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const indice = await carregarIndice(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Mesmos quadros: não trocar poupa replanejar a camada.
    const atual = useRaiosStore.getState().indice;
    useRaiosStore.setState({
      indice: mesmoIndice(atual, indice) ? { ...atual!, gerado: indice.gerado } : indice,
      situacao: { estado: 'ready', erro: null },
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useRaiosStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com a camada em uso. */
export function refreshRaios(): void {
  if (!useRaiosStore.getState().visivel) return;
  tentarDeNovo?.();
  const nuncaCarregou = !jaCarregou && useRaiosStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarRaios(true);
}

export function iniciarSyncRaios(): () => void {
  if (useRaiosStore.getState().visivel) void carregarRaios();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshRaios();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useRaiosStore.subscribe((a, b) => {
      if (!a.visivel || b.visivel) return;
      // Religar depois de um tempo: o índice guardado pode ser de horas atrás.
      if (!jaCarregou) void carregarRaios();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarRaios(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshRaios),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Instante do quadro mais novo publicado, ou null. */
export function ultimoQuadro(indice: IndiceRaios | null): number | null {
  const q = indice?.quadros;
  return q?.length ? q[q.length - 1].t : null;
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useRaiosDesatualizados(): boolean {
  const visivel = useRaiosStore((s) => s.visivel);
  const erro = useRaiosStore((s) => s.situacao.estado === 'error' && !s.indice);
  const ultimo = useRaiosStore((s) => ultimoQuadro(s.indice));
  // Relógio próprio: com a máquina parada o índice não muda.
  const agora = useNow(60_000);
  if (!visivel) return false;
  return erro || avisoDeFrescor(ultimo, agora) !== null;
}

/** Só para os testes. */
export function resetRaiosSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
